import React, { useState, useEffect, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme, Snackbar, TextInput as PaperTextInput, Button } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import { useAuth } from '../context/AuthContext';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import UserService from '../services/userService';
import TruckService from '../services/truckService';
import { DriverAllowlistEntry, UserRole } from '../types/UserProfile';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';
import FloatingActionButton from '../components/FloatingActionButton';
import BaseBottomSheet from '../components/BaseBottomSheet';
import TruckDropdown from '../components/TruckDropdown';

const ROLE_LABELS: Record<UserRole, string> = {
  driver: 'Driver',
  supervisor: 'Supervisor',
};

const DriversScreen: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const [entries, setEntries] = useState<DriverAllowlistEntry[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAddSheet, setShowAddSheet] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole>('driver');
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [editingEntry, setEditingEntry] = useState<DriverAllowlistEntry | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('driver');
  const [editTruck, setEditTruck] = useState<Truck | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setEntries([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [fetchedEntries, activeTrucks] = await Promise.all([
        UserService.getAllowlistEntries(),
        TruckService.getAllActiveTrucks(),
      ]);
      setEntries(fetchedEntries);
      setTrucks(activeTrucks);
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriversScreen.loadData');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddDriver = async () => {
    if (!email || !user?.uid) return;
    if (selectedRole === 'driver' && !selectedTruck) return;

    try {
      setSubmitting(true);
      // Drivers require a vehicle; supervisors may optionally have one (so they
      // can also drive). Include assignedTruckId whenever a truck is selected.
      const entryData: Omit<DriverAllowlistEntry, 'id' | 'createdAt' | 'addedBy'> = {
        email,
        role: selectedRole,
        ...(selectedTruck ? { assignedTruckId: selectedTruck.id! } : {}),
      };

      await UserService.addDriverToAllowlist(entryData, user.uid);
      setShowAddSheet(false);
      setEmail('');
      setSelectedRole('driver');
      setSelectedTruck(null);
      setSnackbarMessage('User added successfully!');
      setSnackbarVisible(true);
      await loadData();
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriversScreen.handleAddDriver');
      setSnackbarMessage('Failed to add user.');
      setSnackbarVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDriver = async (entryId: string) => {
    try {
      await UserService.removeDriverFromAllowlist(entryId);
      setSnackbarMessage('User removed successfully!');
      setSnackbarVisible(true);
      await loadData();
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriversScreen.handleRemoveDriver');
      setSnackbarMessage('Failed to remove user.');
      setSnackbarVisible(true);
    }
  };

  // Sync the edit form when a user row is opened for editing.
  useEffect(() => {
    if (editingEntry) {
      setEditRole(editingEntry.role);
      setEditTruck(trucks.find(t => t.id === editingEntry.assignedTruckId) ?? null);
    }
  }, [editingEntry, trucks]);

  const handleSaveEdit = async () => {
    if (!editingEntry?.id) return;
    if (editRole === 'driver' && !editTruck) {
      setSnackbarMessage('Drivers must have a vehicle assigned.');
      setSnackbarVisible(true);
      return;
    }
    try {
      await UserService.updateAllowlistEntry(editingEntry.id, {
        role: editRole,
        assignedTruckId: editTruck?.id ?? null,
      });
      setEditingEntry(null);
      setSnackbarMessage('User updated!');
      setSnackbarVisible(true);
      await loadData();
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriversScreen.handleSaveEdit');
      setSnackbarMessage('Failed to update user.');
      setSnackbarVisible(true);
    }
  };

  const getTruckPlate = (truckId?: string) => {
    if (!truckId) return '—';
    const truck = trucks.find(t => t.id === truckId);
    return truck ? truck.plateNumber : truckId;
  };

  const isSubmitDisabled =
    !email.trim() || submitting || (selectedRole === 'driver' && !selectedTruck);

  const renderEntry = ({ item }: { item: DriverAllowlistEntry }) => (
    <View style={[styles.entryRow, { backgroundColor: theme.colors.surface }]}>
      <View style={styles.entryInfo}>
        <Text style={[styles.entryEmail, { color: theme.colors.onSurface }]}>
          {item.email}
        </Text>
        {item.assignedTruckId && (
          <Text style={[styles.entryTruck, { color: theme.colors.onSurfaceVariant }]}>
            {getTruckPlate(item.assignedTruckId)}
          </Text>
        )}
      </View>

      <TouchableOpacity
        testID={`role-badge-${item.id}`}
        onPress={() => setEditingEntry(item)}
        style={[
          styles.roleBadge,
          { borderColor: item.role === 'supervisor' ? '#7eefd0' : theme.colors.primary },
        ]}
      >
        <Text
          style={[
            styles.roleBadgeText,
            { color: item.role === 'supervisor' ? '#7eefd0' : theme.colors.primary },
          ]}
        >
          {ROLE_LABELS[item.role ?? 'driver']}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        testID={`delete-entry-${item.id}`}
        onPress={() => handleRemoveDriver(item.id!)}
        style={styles.deleteButton}
      >
        <MaterialCommunityIcons
          name="delete-outline"
          size={22}
          color={theme.colors.error ?? '#B00020'}
        />
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={[styles.headerTitle, { color: theme.colors.onBackground }]}>
            Users
          </Text>
          <Text style={[styles.headerSubtitle, { color: theme.colors.onSurfaceVariant }]}>
            {loading ? 'Loading...' : `${entries.length} user${entries.length !== 1 ? 's' : ''} on allowlist`}
          </Text>
        </View>
      </View>

      <View style={styles.cardWrapper}>
        <LinearGradient
          colors={['#063669', '#274e82']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.heroCard}
        >
          <View style={styles.heroRow}>
            <View style={styles.heroLeft}>
              <Text style={styles.heroLabel}>USER ALLOWLIST</Text>
              <Text style={styles.heroAmount}>{loading ? '—' : entries.length}</Text>
              <Text style={styles.heroSub}>registered user{entries.length !== 1 ? 's' : ''}</Text>
            </View>
            <View style={styles.heroBadge}>
              <Text style={styles.heroBadgeCount}>{loading ? '—' : trucks.length}</Text>
              <Text style={styles.heroBadgeLabel}>trucks</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {!loading && entries.length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons
            name="account-group-outline"
            size={48}
            color={theme.colors.onSurfaceVariant}
          />
          <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>
            No users added yet
          </Text>
          <Text style={[styles.emptySubText, { color: theme.colors.onSurfaceVariant }]}>
            Tap + to add a user to the allowlist
          </Text>
        </View>
      ) : (
        <FlatList
          data={entries}
          keyExtractor={item => item.id ?? item.email}
          renderItem={renderEntry}
          contentContainerStyle={styles.list}
        />
      )}

      <View style={styles.fabContainer}>
        <FloatingActionButton onPress={() => setShowAddSheet(true)} />
      </View>

      {/* Add user bottom sheet */}
      <BaseBottomSheet
        visible={showAddSheet}
        onClose={() => {
          setShowAddSheet(false);
          setEmail('');
          setSelectedRole('driver');
          setSelectedTruck(null);
        }}
        title="Add User"
        height={0.5}
      >
        <ScrollView
          style={styles.formScrollView}
          contentContainerStyle={styles.formContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <PaperTextInput
            mode="flat"
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            testID="email-input"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
          />

          <View style={styles.roleRow}>
            <Text style={[styles.roleLabel, { color: theme.colors.onSurfaceVariant }]}>
              Role
            </Text>
            <View style={styles.rolePicker}>
              {(['driver', 'supervisor'] as UserRole[]).map(role => (
                <TouchableOpacity
                  key={role}
                  testID={`role-option-${role}`}
                  onPress={() => setSelectedRole(role)}
                  style={[
                    styles.roleOption,
                    selectedRole === role && { backgroundColor: theme.colors.primary },
                    { borderColor: theme.colors.primary },
                  ]}
                >
                  <Text
                    style={[
                      styles.roleOptionText,
                      { color: selectedRole === role ? theme.colors.onPrimary : theme.colors.primary },
                    ]}
                  >
                    {ROLE_LABELS[role]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <TruckDropdown
            selectedTruck={selectedTruck}
            onTruckSelect={setSelectedTruck}
            label={
              selectedRole === 'driver'
                ? 'Assign Truck'
                : 'Assign Vehicle (optional)'
            }
            placeholder="Select a truck"
          />
          {selectedRole === 'supervisor' && (
            <Text style={[styles.hintText, { color: theme.colors.onSurfaceVariant }]}>
              A supervisor with a vehicle can also pick up and drive jobs.
            </Text>
          )}

          <Button
            mode="contained"
            onPress={handleAddDriver}
            disabled={isSubmitDisabled}
            testID="submit-add-driver"
            style={styles.submitButton}
          >
            Add User
          </Button>
        </ScrollView>
      </BaseBottomSheet>

      {/* Edit user modal (role + optional vehicle) */}
      <Modal
        visible={editingEntry !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setEditingEntry(null)}
      >
        {editingEntry !== null && (
        <Pressable style={styles.modalBackdrop} onPress={() => setEditingEntry(null)}>
          <Pressable
            style={[styles.rolePickerModal, { backgroundColor: theme.colors.surface }]}
            onPress={() => {}}
          >
            <Text style={[styles.modalTitle, { color: theme.colors.onSurface }]}>
              Edit {editingEntry?.email}
            </Text>
            {(['driver', 'supervisor'] as UserRole[]).map(role => (
              <TouchableOpacity
                key={role}
                testID={`edit-role-option-${role}`}
                onPress={() => setEditRole(role)}
                style={[
                  styles.modalRoleOption,
                  editRole === role && { backgroundColor: theme.colors.primaryContainer },
                ]}
              >
                <Text style={[styles.modalRoleText, { color: theme.colors.onSurface }]}>
                  {ROLE_LABELS[role]}
                </Text>
                {editRole === role && (
                  <MaterialCommunityIcons name="check" size={18} color={theme.colors.primary} />
                )}
              </TouchableOpacity>
            ))}

            <TruckDropdown
              selectedTruck={editTruck}
              onTruckSelect={setEditTruck}
              label={editRole === 'driver' ? 'Assign Truck' : 'Assign Vehicle (optional)'}
              placeholder="Select a truck"
            />

            <Button
              mode="contained"
              onPress={handleSaveEdit}
              testID="save-edit-user"
              style={styles.submitButton}
            >
              Save
            </Button>
          </Pressable>
        </Pressable>
        )}
      </Modal>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setSnackbarVisible(false) }}
      >
        {snackbarMessage}
      </Snackbar>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  headerLeft: { flex: 1, marginRight: 12 },
  headerTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 22,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    marginTop: 3,
    lineHeight: 18,
  },
  cardWrapper: {
    paddingHorizontal: 20,
    marginBottom: 14,
  },
  heroCard: {
    borderRadius: 6,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  heroLeft: { flex: 1 },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
    lineHeight: 40,
  },
  heroSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
    marginTop: 2,
  },
  heroBadge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  heroBadgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  heroBadgeLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  emptyText: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
    textAlign: 'center',
  },
  emptySubText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  list: {
    paddingHorizontal: 16,
    gap: 8,
  },
  entryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 14,
    elevation: 1,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
  },
  entryInfo: {
    flex: 1,
    gap: 4,
  },
  entryEmail: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
  },
  entryTruck: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
  },
  roleBadge: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginRight: 8,
  },
  roleBadgeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
  },
  deleteButton: {
    padding: 6,
  },
  fabContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  formScrollView: {
    flexShrink: 1,
  },
  formContent: {
    padding: 16,
    gap: 16,
  },
  input: {
    marginBottom: 4,
  },
  hintText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    marginTop: -8,
  },
  roleRow: {
    gap: 8,
  },
  roleLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
  },
  rolePicker: {
    flexDirection: 'row',
    gap: 8,
  },
  roleOption: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
  },
  roleOptionText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
  },
  submitButton: {
    marginTop: 8,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  rolePickerModal: {
    width: '100%',
    borderRadius: 12,
    padding: 20,
    gap: 4,
  },
  modalTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 15,
    marginBottom: 12,
  },
  modalRoleOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  modalRoleText: {
    fontFamily: 'Inter-Regular',
    fontSize: 15,
  },
});

export default DriversScreen;
